import * as angular from 'angular';
import * as moment from 'moment';

import { BadActor, TagDateValue } from '../../models';
import { BusinessKpi, InputValuesDTO } from '../../models/DTO';
import { AggregationMode } from '../../models/enum';
import { IDateRangeService } from '../general';
import { IwidgetIntervalHelperService } from '../helpers';
import { IStatisticsService } from './';
import { IRequestService } from '../../services';

export interface IInputValuesService {
    count(): angular.IPromise<number>;

    getAllBetweenDates(beginDate?, endDate?): angular.IPromise<any>;
    getBetweenDatesForTag(tagDefinitionGuid: string, beginDate?, endDate?): angular.IPromise<any>;
    getAverageBetweenDatesForTag(tagDefinitionGuid: string): angular.IPromise<any>;
    getValueBetweenDatesForTag(tagDefinitionGuid: string,
        value: string,
        beginDate?: string,
        endDate?: string): angular.IPromise<any>;
    getAggregatedValueBetweenDatesForTag(tagDefinitionGuid: string,
        value: string,
        beginDate?: string,
        endDate?: string,
        aggregationMode?: AggregationMode): angular.IPromise<BusinessKpi>;
    //getCompressedAverageBetweenDatesForTag(tagDefinitionGuid: string): angular.IPromise<any>;
    getCompressedValueBetweenDatesForTag(tagDefinitionGuid: string,
        value: string,
        beginDate?: string,
        endDate?: string): angular.IPromise<TagDateValue[]>;
    getBetweenDatesForTagList(tagGuidList: any[]): angular.IPromise<any>;
    //getCsv(tagDefinitionGuid: string, fileNamePrefix?: string, beginDate?, endDate?): angular.IPromise<any>;

    getPercentageOfGoodValues(guid?: string): angular.IPromise<number>;
    getPercentageOfGoodValuesBetweenDates(beginDate: string, endDate: string): angular.IPromise<number>;
    getPercentageOfGoodValuesForTag(guid?: string): angular.IPromise<number>;
    getPercentageOfGoodValuesPerDate(guid?: string): angular.IPromise<any>;
    getNumberOfBadValuesPerTag(guid?: string, max?: number): angular.IPromise<any>;
    getPercentageOfBadValuesPerType(guid?: string, max?: number): angular.IPromise<any>;
    //getPercentageBadValuesPerTag(): angular.IPromise<any>; // migrated

    getPercentageOfBadValues(guid?: string): angular.IPromise<number>;
    getPercentageOfBadValuesBetweenDates(beginDate: string, endDate: string): angular.IPromise<number>;
    getPercentageOfBadValuesForTag(guid?: string): angular.IPromise<number>;
    getPercentageOfBadValuesPerDate(guid?: string): angular.IPromise<any>;

    getPercentageOfFlat(guid?: string): angular.IPromise<number>;
    getPercentageOfFlatBetweenDates(beginDate: string, endDate: string): angular.IPromise<number>;
    getPercentageOfFlatPerDate(guid?: string): angular.IPromise<any>;
    getNumberOfFlatPerTag(guid?: string): angular.IPromise<any>;

    getPercentageOfScatter(guid?: string): angular.IPromise<number>;
    getPercentageOfScatterBetweenDates(beginDate: string, endDate: string): angular.IPromise<number>;
    getPercentageOfScatterPerDate(guid?: string): angular.IPromise<any>;
    getNumberOfScatterPerTag(guid?: string): angular.IPromise<any>;

    getPercentageOfOutOfRange(guid?: string): angular.IPromise<number>;
    getPercentageOfOutOfRangeBetweenDates(beginDate: string, endDate: string): angular.IPromise<number>;
    getPercentageOfOutOfRangePerDate(guid?: string): angular.IPromise<any>;
    getNumberOfOutOfRangePerTag(guid?: string): angular.IPromise<any>;

    getStatistics(guid: string, force?: boolean): angular.IPromise<any>;
    getInvalidKPIs(guid?: string): angular.IPromise<number>;
    getInvalidKPIsBetweenDates(beginDate: string, endDate: string): angular.IPromise<number>;
    getPercentageofInvalidKpisPerDate(guid: string): angular.IPromise<any>;
    getPercentageofInvalidKpisPerKPI(guid: string): angular.IPromise<any>;
    getPercentageInvalidForIdentifier(guid: string): angular.IPromise<any>;
    getNumberOfInvalidPerKPI(guid: string, max?: number): angular.IPromise<BadActor[]>;
    getNumberOfInvalidPrecedents(guid?: string): angular.IPromise<BadActor[]>;

    getPercentageOfSuspiciousKpis(guid?: string): angular.IPromise<number>;
    getPercentageOfSuspiciousKpisPerDate(guid?: string): angular.IPromise<any>;
    getNumberOfSuspiciousKpisPerKPI(guid?: string, max?: number): angular.IPromise<any>;
    getNumberOfSuspiciousPrecedents(guid: string): angular.IPromise<BadActor[]>;
    getPercentageSuspiciousForIdentifierBetweenDates(beginDate: string, endDate: string, guid?: string): angular.
        IPromise<number>;
    getPercentageOfSuspiciousKpisBetweenDates(beginDate: string, endDate: string, guid?: string): angular.
        IPromise<number>;
    getPercentageSuspiciousForIdentifier(guid: string): angular.IPromise<number>;
    getManualInputs(guids: string[]): angular.IPromise<InputValuesDTO[]>;
    getValueAndStatus(guid: string, endDate): angular.IPromise<any>;
}

export class InputValuesService implements IInputValuesService {
    static serviceId = 'inputValuesService';

    static $inject = [
        '$q',
        'requestService',
        'dateRangeService',
        'statisticsService',
        'widgetIntervalHelperService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _dateRangeService: IDateRangeService,
        private _statisticsService: IStatisticsService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService) {
    }

    count(): angular.IPromise<number> {
        const url = this._requestService.getUrlV1() + 'inputValues/count/';
        return this._requestService.get(url);
    }

    getAllBetweenDates(beginDate?: Date, endDate?: Date): angular.IPromise<any> {
        if (!beginDate) {
            beginDate = this._dateRangeService.beginDate() as Date;
        }
        if (!endDate) {
            endDate = this._dateRangeService.endDate() as Date;
        }
        const url = this._requestService.getUrlV1() + 'inputValues/'
            + '?beginDateTime=' + moment(beginDate).format('YYYY-MM-DD')
            + '&endDateTime=' + moment(endDate).format('YYYY-MM-DD');
        return this._requestService.get(url);
    }

    getBetweenDatesForTag(tagDefinitionGuid: string, beginDate?, endDate?, force: boolean = false): angular.IPromise<any> {
        if (!beginDate) {
            beginDate = this._dateRangeService.beginDate() as string;
        }
        if (!endDate) {
            endDate = this._dateRangeService.endDate() as string;
        }
        const url = this._requestService.getUrlV1() + 'inputValues/'
            + '?tagDefinitionGuid=' + tagDefinitionGuid
            + '&beginDateTime=' + beginDate
            + '&endDateTime=' + endDate;
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url);
    }

    getAverageBetweenDatesForTag(tagDefinitionGuid: string): angular.IPromise<any> {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        const url = this._requestService.getUrlV1() + 'inputValues/GetAverage/'
            + '?tagDefinitionGuid=' + tagDefinitionGuid
            + '&beginDateTime=' + beginDate
            + '&endDateTime=' + endDate;
        return this._requestService.get(url);
    }

    getValueBetweenDatesForTag(tagDefinitionGuid: string,
        value: string,
        beginDate?: string,
        endDate?: string): angular.IPromise<any> {
        if (this._dateRangeService.initialized()) {
            if (!beginDate && !endDate) {
                beginDate = this._dateRangeService.beginDate() as string;
                endDate = this._dateRangeService.endDate() as string;
            }
            const url = this._requestService.getUrlV1() + 'inputValues/GetValue/'
                + '?tagDefinitionGuid=' + tagDefinitionGuid
                + '&beginDateTime=' + beginDate
                + '&endDateTime=' + endDate
                + '&value=' + value;
            return this._requestService.get(url);
        }
    }

    getCompressedValueBetweenDatesForTag(tagDefinitionGuid: string,
        value: string,
        beginDate?: string,
        endDate?: string): angular.IPromise<TagDateValue[]> {
        beginDate = beginDate ? beginDate : this._dateRangeService.beginDate() as string;
        endDate = endDate ? endDate : this._dateRangeService.endDate() as string;
        const url = this._requestService.getUrlV1() + 'inputValues/GetCompressedValue/'
            + '?tagDefinitionGuid=' + tagDefinitionGuid
            + '&beginDateTime=' + beginDate
            + '&endDateTime=' + endDate
            + '&value=' + value;
        return this._requestService.get(url);
    }

    getBetweenDatesForTagList(tagGuidList: any[]): angular.IPromise<any> {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        const url = this._requestService.getUrlV1() + 'inputValues/multipleTags/'
            + '?beginDateTime=' + beginDate
            + '&endDateTime=' + endDate;
        return this._requestService.post(url, tagGuidList);
    }

    getPercentageOfGoodValues(guid?: string): angular.IPromise<number> {
        return this.getAtAddress('getPercentageGoodValues', guid);
    }

    getPercentageOfGoodValuesBetweenDates(beginDate: string, endDate: string): angular.IPromise<number> {
        return this.getAtAddressBetweenDates('getPercentageGoodValues', beginDate, endDate);
    }

    getPercentageOfGoodValuesForTag(guid: string): angular.IPromise<number> {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        const url = this._requestService.getUrlV1() + 'inputValues/GetPercentageGoodValuesForTag/?'
            + (guid ? (`tagDefinitionGuid=${guid}&`) : '')
            + 'beginDate=' + beginDate
            + '&endDate=' + endDate;
        return this._requestService.get(url);
    }

    getPercentageOfGoodValuesPerDate(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('getPercentageGoodValuesPerDate', guid);
    }

    getNumberOfBadValuesPerTag(guid?: string, max: number = 10): angular.IPromise<any> {
        return this.getAtAddress('getNumberBadValuesPerTag', guid, max);
    }

    getPercentageOfBadValuesPerType(guid?: string, max: number = 10): angular.IPromise<any> {
        return this.getAtAddress('getPercentageBadValuesPerType', guid, max);
    }

    getPercentageOfFlat(guid?: string): angular.IPromise<number> {
        return this.getAtAddress('getPercentageFlat', guid);
    }

    getPercentageOfFlatBetweenDates(beginDate: string, endDate: string): angular.IPromise<number> {
        return this.getAtAddressBetweenDates('getPercentageFlat', beginDate, endDate);
    }

    getPercentageOfFlatPerDate(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('getPercentageFlatPerDate', guid);
    }

    getNumberOfFlatPerTag(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('getNumberFlatPerTag', guid);
    }

    getPercentageOfScatter(guid?: string): angular.IPromise<number> {
        return this.getAtAddress('getPercentageScatter', guid);
    }

    getPercentageOfScatterBetweenDates(beginDate: string, endDate: string, guid?: string): angular.
        IPromise<number> {
        return this.getAtAddressBetweenDates('getPercentageScatter', beginDate, endDate, guid);
    }

    getPercentageOfScatterPerDate(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetPercentageScatterPerDate', guid);
    }

    getNumberOfScatterPerTag(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetNumberScatterPerTag', guid);
    }

    getStatistics(guid: string, force: boolean = false): angular.IPromise<any> {
        const defered = this._$q.defer();
        if (this._dateRangeService.initialized()) {
            const self = this;
            const interval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();

            this.getBetweenDatesForTag(guid, interval.beginDate, interval.endDate, force)
                .then(data => {
                    const result = self._statisticsService.getStatistics(data);
                    defered.resolve(result);
                });
        } else {
            defered.resolve();
        }
        return defered.promise;
    }

    getPercentageOfOutOfRange(guid?: string): angular.IPromise<number> {
        return this.getAtAddress('GetPercentageOutOfRange', guid);
    }

    getPercentageOfOutOfRangeBetweenDates(beginDate: string, endDate: string): angular.IPromise<number> {
        return this.getAtAddressBetweenDates('GetPercentageOutOfRange', beginDate, endDate);
    }

    getPercentageOfOutOfRangePerDate(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetPercentageOutOfRangePerDate', guid);
    }

    getNumberOfOutOfRangePerTag(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetNumberOutOfRangePerTag', guid);
    }

    getPercentageOfBadValues(guid?: string): angular.IPromise<number> {
        return this.getAtAddress('GetPercentageBadValues', guid);
    }

    getPercentageOfBadValuesBetweenDates(beginDate: string, endDate: string): angular.IPromise<number> {
        return this.getAtAddressBetweenDates('GetPercentageBadValues', beginDate, endDate);
    }

    getPercentageOfBadValuesForTag(guid?: string): angular.IPromise<number> {
        //return this.getAtAddress('GetPercentageBadValuesForTag', guid);
        if (this._dateRangeService.initialized()) {
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            const url = this._requestService.getUrlV1() + 'inputValues/GetPercentageBadValuesForTag' + '?'
                + (guid ? (`tagDefinitionGuid=${guid}&`) : '')
                + 'beginDate=' + beginDate
                + '&endDate=' + endDate;
            return this._requestService.get(url);
        } else {
            const defered = this._$q.defer<number>();
            defered.resolve(0);
            return defered.promise;
        }
    }

    getPercentageOfBadValuesPerDate(guid?: string): angular.IPromise<any> {
        return this.getAtAddress('GetPercentageBadValuesPerDate', guid);
    }

    getAtAddress(method: string, guid?: string, max?: number): angular.IPromise<any> {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        return this.getAtAddressBetweenDates(method, beginDate, endDate, guid, max);
    }

    getAtAddressBetweenDates(method: string,
        beginDate: string,
        endDate: string,
        guid?: string,
        max?: number): angular.IPromise<any> {

        if (beginDate && endDate) {
            const url = this._requestService.getUrlV1() + 'inputValues/' + method + '/?'
                + (guid ? (`groupGuid=${guid}&`) : '')
                + 'beginDate=' + beginDate
                + '&endDate=' + endDate
                + ((typeof max === 'undefined' || max === null) ? '' : `&max=${max}`);

            return this._requestService.get(url);
        } else {
            const defered = this._$q.defer();
            defered.resolve(0);
            return defered.promise;
        }
    }

    getInvalidKPIs(guid?: string): angular.IPromise<number> {
        return this.getAtInvalidKPIs('percentage', guid);
    }

    getInvalidKPIsBetweenDates(beginDate: string, endDate: string): angular.IPromise<number> {
        return this.getAtBetweenDates('invalidKpis', 'percentage', beginDate, endDate);
    }

    getPercentageofInvalidKpisPerDate(guid: string): angular.IPromise<any> {
        return this.getAtInvalidKPIs('percentagePerDate', guid);
    }

    getPercentageofInvalidKpisPerKPI(guid: string): angular.IPromise<any> {
        return this.getAtInvalidKPIs('percentagePerKPI', guid);
    }

    getNumberOfInvalidPerKPI(guid?: string, max: number = 10): angular.IPromise<any> {
        return this.getAtInvalidKPIs('numberPerKPI', guid, max);
    }

    getNumberOfInvalidPrecedents(guid?: string): angular.IPromise<BadActor[]> {
        const defered = this._$q.defer<BadActor[]>(); //TODO
        const result: BadActor[] = [];
        for (let i = 0; i < 10; i++) {
            result.push({
                name: `Tag${i}`,
                guid: i,
                value: 639
            });
        }
        defered.resolve(result);
        return defered.promise;
    }

    getPercentageOfSuspiciousKpis(guid: string): angular.IPromise<number> {
        return this.getAt('suspiciousKpis', 'percentage', guid);
    }

    getPercentageOfSuspiciousKpisPerDate(guid?: string): angular.IPromise<any> {
        return this.getAt('suspiciousKpis', 'percentagePerDate', guid);
    }

    getNumberOfSuspiciousKpisPerKPI(guid: string, max: number = 10): angular.IPromise<any> {
        return this.getAt('suspiciousKpis', 'numberPerKPI', guid, max);
    }

    private getAtInvalidKPIs(method: string, guid?: string, max?: number): angular.IPromise<any> {
        return this.getAt('invalidKpis', method, guid, max);
    }

    getNumberOfSuspiciousPrecedents(guid: string): angular.IPromise<BadActor[]> {
        const defered = this._$q.defer<BadActor[]>(); //TODO
        const result: BadActor[] = [];
        for (let i = 0; i < 10; i++) {
            result.push({
                name: `Tag${i}`,
                guid: i,
                value: 369
            });
        }
        defered.resolve(result);
        return defered.promise;
    }

    getPercentageOfSuspiciousKpisBetweenDates(beginDate: string, endDate: string, guid?: string): angular.IPromise<number> {
        return this.getAtBetweenDates('suspiciousKPIs', 'percentage', beginDate, endDate, guid);
    }

    private getAt(controller: string, method: string, guid?: string, max?: number): angular.IPromise<any> {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        return this.getAtBetweenDates(controller, method, beginDate, endDate, guid, max);
    }

    private getAtBetweenDates(controller: string,
        method: string,
        beginDate: string,
        endDate: string,
        guid?: string,
        max?: number): angular.IPromise<any> {
        if (beginDate && endDate) {
            const url = this._requestService.getUrlV1() + controller + '/' + method + '?'
                + (guid ? (`groupGuid=${guid}&`) : '')
                + 'beginDate=' + beginDate
                + '&endDate=' + endDate
                + ((typeof max === 'undefined' || max === null) ? '' : `&max=${max}`);
            return this._requestService.get(url);
        }
    }

    getPercentageInvalidForIdentifier(guid: string): angular.IPromise<any> {
        return this.getPercentageForIdentifier(guid, 'invalidKPIs');
    }

    getPercentageSuspiciousForIdentifier(guid: string): angular.IPromise<number> {
        return this.getPercentageForIdentifier(guid, 'suspiciousKPIs');
    }

    getPercentageSuspiciousForIdentifierBetweenDates(beginDate: string, endDate: string, guid?: string): angular.IPromise<number> {
        return this.update('suspiciousKPIs', 'percentageForidentifier', { identifierGuid: guid }, beginDate, endDate);
    }

    getPercentageForIdentifierBetweenDates(beginDate: string,
        endDate: string,
        guid: string,
        controller: string): angular.IPromise<number> {
        return this.update(controller, 'percentageForidentifier', { identifierGuid: guid }, beginDate, endDate);
    }

    getPercentageForIdentifier(guid: string, controller: string): angular.IPromise<number> {
        if (this._dateRangeService.initialized()) {
            //const beginDate = this.dateRangeService.beginDate();
            //const endDate = this.dateRangeService.endDate();
            //if (beginDate && endDate) {
            const params = {
                identifierGuid: guid,
                //beginDate: beginDate,
                //endDate: endDate
            };
            return this.update(controller, 'percentageForidentifier', params);
            //}
        } else {
            const defered = this._$q.defer<number>();
            defered.reject({
                message: 'Dates to load are not yet available'
            });
            return defered.promise;
        }
    }

    private update(controller: string, method: string, params: any, beginDate?: string, endDate?: string) {
        // initialize default date if args are not provided
        params = params || {};
        if (!beginDate) {
            params.beginDate = this._dateRangeService.beginDate() as string;
        }
        if (!endDate) {
            params.endDate = this._dateRangeService.endDate() as string;
        }

        // ?identifierGuid=${guid}&beginDate=${beginDate}&endDate=${endDate}
        const url = `${this._requestService.getUrlV1()}${controller}/${method}/`;
        return this._requestService.get(url, params);
    }

    getAggregatedValueBetweenDatesForTag(tagDefinitionGuid: string, value: string, beginDate?: string, endDate?: string, aggregationMode?: AggregationMode): angular.IPromise<BusinessKpi> {
        if (this._dateRangeService.initialized()) {
            beginDate = beginDate ? beginDate : this._dateRangeService.beginDate() as string;
            endDate = endDate ? endDate : this._dateRangeService.endDate() as string;
            const params = {
                tagDefinitionGuid: tagDefinitionGuid,
                beginDateTime: beginDate,
                endDateTime: endDate,
                value: value,
                aggregationMode: aggregationMode ? aggregationMode : 1
            };
            const url = this._requestService.getUrlV1() + 'inputValues/getAggregatedValueForTag/';
            return this._requestService.get(url, params);
        } else {
            return this._$q.reject('Date range not initialized.');
        }
    }

    getManualInputs(guids: string[]): angular.IPromise<InputValuesDTO[]> {
        if (this._dateRangeService.initialized()) {
            const url = this._requestService.getUrlV1() + 'inputValues/getManualInputs?beginDateTime/='
                + this._dateRangeService.beginDate() as string + '&endDateTime=' + this._dateRangeService.endDate() as string;
            return this._requestService.post(url, guids);
        } else {
            return this._$q.reject('Date range not initialized.');
        }
    }

    getValueAndStatus(guid: string, endDate): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'inputValues/getValueAndStatus/';
        const params = {
            tagDefinitionGuid: guid,
            dateTime: endDate
        };
        return this._requestService.get(url, params);
    }
}